import type { BulkChatTagsEntry } from '@ivanamato/messaging-inbox';
import '../contracts/MessagingChatClientes_BulkLookupInput';
import '../contracts/MessagingChatClientes_BulkLookupResponse';
import '../contracts/MessagingChatClientes_LinkInput';
import '../contracts/MessagingChatClientes_LinkResponse';
import '../contracts/MessagingChatClientes_LookupInput';
import '../contracts/MessagingChatClientes_LookupResponse';
import '../contracts/MessagingChatClientes_UnlinkInput';
import '../contracts/MessagingChatClientes_UnlinkResponse';
import { useI18n } from 'vue-i18n'
import { httpService } from '../services/httpService'
import { eventBus } from '../eventBus'
import { log } from '../utils/DebugLogger'

// In-memory cache shared across calls (cleared on link/unlink)
const linkCache = new Map()

// Cache TTL: entries older than 5 minutes are treated as uncached
const LINK_CACHE_TTL_MS = 5 * 60 * 1000

// Short TTL applied to phones when a bulk_lookup request fails.
// Prevents immediate retry storms after a timeout — phones will re-fetch after 30s.
const LINK_CACHE_FAILURE_TTL_MS = 30 * 1000

// Set of phones currently being fetched by an in-flight bulk_lookup request.
// Prevents duplicate concurrent requests for the same phones.
const pendingPhones = new Set()

// Module-level cache invalidation — runs once on import
eventBus.on('orcamento.saved', () => linkCache.clear())
eventBus.on('compromisso.saved', () => linkCache.clear())
eventBus.on('cliente.saved', () => linkCache.clear())

// Returns cached data if still within TTL, or null if missing/expired
const getCached = (phone: any) => {
  const entry = linkCache.get(phone)
  if (!entry) return null
  if (Date.now() - entry.cachedAt > LINK_CACHE_TTL_MS) {
    linkCache.delete(phone)
    return null
  }
  return entry.data
}

export function useMessagingChatCliente() {
  const { t } = useI18n()

  const lookupChatCliente = async (phone: string, deviceId: number | undefined) => {
    const normalized = phone.replace(/\D/g, '')

    const cached = getCached(normalized)
    if (cached !== null) {
      return cached
    }

    try {
      const response = await httpService.post(
        '/index.php/config/messaging_chat_clientes/lookup',
        { phone: normalized, device_id: deviceId },
        { inputContract: 'MessagingChatClientes_LookupInput', responseContract: 'MessagingChatClientes_LookupResponse' }
      )

      if (response.success && response.dados) {
        const dados = response.dados
        const firstCliente = dados.linked && dados.clientes?.length > 0 ? dados.clientes[0] : null
        const entry = {
          linked: dados.linked,
          has_orcamento: firstCliente?.has_orcamento || false,
          has_compromisso_aberto: firstCliente?.has_compromisso_aberto || false,
          is_fidelizado: firstCliente?.is_fidelizado || false,
          clientes: dados.clientes || [],
          auto_match_candidates: dados.auto_match_candidates || [],
        }
        linkCache.set(normalized, { data: entry, cachedAt: Date.now() })
        return entry
      }
    } catch (err) {
      log('useMessagingChatCliente', 'lookupChatCliente', 'Error', err, 'error')
    }

    return { linked: false, has_orcamento: false, has_compromisso_aberto: false, is_fidelizado: false, clientes: [], auto_match_candidates: [] }
  }

  const linkChatCliente = async (phone: string, clienteId: number, linkType: string = 'manual', deviceId: number | null = null) => {
    const normalized = phone.replace(/\D/g, '')

    try {
      const response = await httpService.post(
        '/index.php/config/messaging_chat_clientes/link',
        {
          phone: normalized,
          cliente_id: clienteId,
          link_type: linkType,
          device_id: deviceId,
        },
        { inputContract: 'MessagingChatClientes_LinkInput', responseContract: 'MessagingChatClientes_LinkResponse' }
      )

      // Clear cache for this phone so next lookup fetches fresh data
      linkCache.delete(normalized)

      return response
    } catch (err) {
      log('useMessagingChatCliente', 'linkChatCliente', 'Error', err, 'error')
      throw err
    }
  }

  const unlinkChatCliente = async (phone: string, clienteId: number) => {
    const normalized = phone.replace(/\D/g, '')

    try {
      const response = await httpService.post(
        '/index.php/config/messaging_chat_clientes/unlink',
        { phone: normalized, cliente_id: clienteId },
        { inputContract: 'MessagingChatClientes_UnlinkInput', responseContract: 'MessagingChatClientes_UnlinkResponse' }
      )

      linkCache.delete(normalized)

      return response
    } catch (err) {
      log('useMessagingChatCliente', 'unlinkChatCliente', 'Error', err, 'error')
      throw err
    }
  }

  // Build actions for a linked client
  const buildLinkedActions = (cliente: any, phone: any) => {
    return [
      {
        id: 'edit-customer',
        label: cliente.cliente_nome,
        onClick: () => {
          eventBus.emit('modal.cliente.open', { id: cliente.cliente_id })
        },
      },
      {
        id: 'new-budget',
        label: t('Novo Orçamento'),
        onClick: () => {
          eventBus.emit('modal.orcamentos.open', { clienteId: cliente.cliente_id })
        },
      },
      {
        id: 'schedule-message',
        label: t('Agendar mensagem'),
        onClick: () => {
          eventBus.emit('modal.mensagens-programadas-wizard.open', {
            selectedClientes: [{
              cliente_id: cliente.cliente_id,
              cliente_nome: cliente.cliente_nome,
              cliente_franquia_id: cliente.cliente_franquia_id,
              franquia: cliente.franquia || '',
              localidade: '',
              cliente_bairro: cliente.cliente_bairro || '',
              cliente_telefone: cliente.cliente_telefone || '',
              cliente_ddd: cliente.cliente_ddd || '',
              cliente_ddi: cliente.cliente_ddi || '55',
            }],
          })
        },
      },
      {
        id: 'unlink',
        label: t('Desvincular Cliente'),
        onClick: () => {
          unlinkChatCliente(phone, cliente.cliente_id).then(() => {
            eventBus.emit('globalMessage.success', t('Cliente desvinculado'))
          }).catch((err: any) => {
            log('useMessagingChatCliente', 'unlink', 'Error', err, 'error')
            eventBus.emit('globalMessage.error', t('Erro ao desvincular cliente'))
          })
        },
      },
    ]
  }

  // Build actions when auto-match candidates exist
  const buildAutoMatchActions = (candidates: any, phone: any, deviceId: any, contactName: any) => {
    const actions = []

    for (const candidate of candidates) {
      const franquiaLabel = candidate.franquia ? ` (${candidate.franquia})` : ''
      actions.push({
        id: `auto-link-${candidate.cliente_id}`,
        label: `${t('Vincular')}: ${candidate.cliente_nome}${franquiaLabel}`,
        onClick: () => {
          linkChatCliente(phone, candidate.cliente_id, 'auto', deviceId).then(() => {
            eventBus.emit('globalMessage.success', t('Cliente vinculado com sucesso'))
          }).catch((err: any) => {
            log('useMessagingChatCliente', 'autoLink', 'Error', err, 'error')
            eventBus.emit('globalMessage.error', t('Erro ao vincular cliente'))
          })
        },
      })
    }

    actions.push({
      id: 'search-customer',
      label: t('Encontre um cliente'),
      onClick: () => {
        log('useMessagingChatCliente', 'searchCustomer', 'Opening vincula-cliente modal', phone)
        eventBus.emit('modal.vincula-cliente.open', { phone, deviceId, contactName })
      },
    })

    actions.push({
      id: 'new-customer',
      label: t('Novo Cliente'),
      onClick: () => {
        eventBus.emit('modal.cliente.open', {
          prefillPhone: phone,
          prefillName: contactName || '',
        })
      },
    })

    return actions
  }

  // Build actions when no links and no candidates
  const buildUnlinkedActions = (phone: any, contactName: any, deviceId: any) => {
    return [
      {
        id: 'search-customer',
        label: t('Encontre um cliente'),
        onClick: () => {
          log('useMessagingChatCliente', 'searchCustomer', 'Opening vincula-cliente modal', phone)
          eventBus.emit('modal.vincula-cliente.open', { phone, deviceId, contactName })
        },
      },
      {
        id: 'new-customer',
        label: t('Novo Cliente'),
        onClick: () => {
          eventBus.emit('modal.cliente.open', {
            prefillPhone: phone,
            prefillName: contactName || '',
          })
        },
      },
    ]
  }

  /**
   * Async resolver for chatActions in @ivanamato/messaging-inbox.
   * Signature: (chat: Chat, device: DeviceConfig) => Promise<ChatAction[]>
   *
   * For non-WhatsApp channels (Instagram), chat-client linking is not
   * supported yet (Instagram uses user IDs, not phone numbers).
   */
  const resolveChatActions = async (chat: any, device: any) => {
    // Instagram devices: no chat-client linking support yet (uses user IDs, not phones)
    if (device?.channel !== 'whatsapp') {
      return []
    }

    const phone = chat.phoneNumber.replace(/\D/g, '')
    const deviceId = device?.id || null
    const contactName = chat.contactName || ''

    log('useMessagingChatCliente', 'resolveChatActions', 'Resolving for', { phone, deviceId, contactName })

    // If already known to be linked, use the cache — no HTTP call needed
    const cached = getCached(phone)
    if (cached?.linked && cached.clientes.length > 0) {
      return buildLinkedActions(cached.clientes[0], phone)
    }

    // For unlinked or unknown: bypass cache and call the single lookup endpoint.
    // bulk_lookup (used for tags) never fetches auto_match_candidates, so the
    // cached entry for unlinked phones always has candidates: []. We need a fresh
    // single lookup here to get the actual candidate suggestions.
    linkCache.delete(phone)
    const result = await lookupChatCliente(phone, deviceId)

    if (result.linked && result.clientes.length > 0) {
      return buildLinkedActions(result.clientes[0], phone)
    }

    if (result.auto_match_candidates && result.auto_match_candidates.length > 0) {
      return buildAutoMatchActions(result.auto_match_candidates, phone, deviceId, contactName)
    }

    return buildUnlinkedActions(phone, contactName, deviceId)
  }

  /**
   * Derive tag pills from a cached lookup result.
   * Priority: open compromisso > fidelizado > has orcamento > plain linked.
   */
  const deriveTags = (result: any) => {
    if (!result.linked) return [{ id: 'nao_vinculado', label: t('Relacionar'), color: '#6b7280', background: '#f3f4f6' }]

    const tags = [{ id: 'vinculado', label: t('Relacionado'), color: '#0369a1', background: '#e0f2fe' }]

    if (result.has_orcamento) {
      tags.push({ id: 'com_orcamento', label: t('Com orçamento'), color: '#2563eb', background: '#dbeafe' })
    }
    if (result.has_compromisso_aberto) {
      tags.push({ id: 'compromisso_aberto', label: t('Agendado'), color: '#d97706', background: '#fef3c7' })
    }
    if (result.is_fidelizado) {
      tags.push({ id: 'fidelizado', label: t('Fidelizado'), color: '#059669', background: '#d1fae5' })
    }

    return tags
  }

  /**
   * Async resolver for chatTags in @ivanamato/messaging-inbox.
   * Shows the client state as a colored tag pill on each chat row.
   *
   * For non-WhatsApp channels, return empty tags (phone-based lookup
   * doesn't apply to Instagram user IDs).
   */
  const resolveChatTags = async (chat: any, device: any) => {
    if (device?.channel !== 'whatsapp') {
      return []
    }

    const phone = chat.phoneNumber.replace(/\D/g, '')
    const deviceId = device?.id || null

    const result = await lookupChatCliente(phone, deviceId)
    return deriveTags(result)
  }

  /**
   * Bulk resolver for chatTagsBulk in @ivanamato/messaging-inbox.
   * Resolves all chat tags in a single HTTP request instead of N individual calls.
   * Uses the shared linkCache so already-resolved phones skip the network call.
   *
   * For non-WhatsApp providers, returns empty tags.
   *
   * @param {Array<{key: string, chat: Chat, device: DeviceConfig}>} entries
   * @returns {Promise<Map<string, ChatTag[]>>}
   */
  const resolveChatTagsBulk = async (entries: BulkChatTagsEntry[]) => {
    if (entries.length === 0) return new Map()

    // Separate WhatsApp entries from non-WhatsApp (e.g. Instagram)
    // Discriminate by device.channel since all providers now use providerType 'generic-server'
    const whatsappEntries = []
    const nonWhatsappEntries = []
    for (const entry of entries) {
      if ((entry.device as (typeof entry.device) & { channel?: string })?.channel === 'whatsapp') {
        whatsappEntries.push(entry)
      } else {
        nonWhatsappEntries.push(entry)
      }
    }

    // Collect phones not already in cache and not already being fetched
    const uncachedPhones = []
    for (const entry of whatsappEntries) {
      const phone = entry.chat.phoneNumber.replace(/\D/g, '')
      if (getCached(phone) === null && !pendingPhones.has(phone)) {
        uncachedPhones.push(phone)
      }
    }

    // One HTTP call for all uncached phones
    if (uncachedPhones.length > 0) {
      // Mark as pending to block concurrent duplicate requests
      for (const phone of uncachedPhones) pendingPhones.add(phone)

      try {
        const response = await httpService.post(
          '/index.php/config/messaging_chat_clientes/bulk_lookup',
          { phones: uncachedPhones },
          { inputContract: 'MessagingChatClientes_BulkLookupInput', responseContract: 'MessagingChatClientes_BulkLookupResponse' }
        )

        if (response.success && response.dados) {
          for (const [phone, data] of Object.entries(response.dados)) {
            linkCache.set(phone, {
              data: {
                linked: (data as any).linked,
                has_orcamento: (data as any).has_orcamento || false,
                has_compromisso_aberto: (data as any).has_compromisso_aberto || false,
                is_fidelizado: (data as any).is_fidelizado || false,
                clientes: (data as any).linked ? [{
                  cliente_id: (data as any).cliente_id,
                  cliente_nome: (data as any).cliente_nome,
                  cliente_franquia_id: (data as any).cliente_franquia_id || null,
                }] : [],
                auto_match_candidates: [],
              },
              cachedAt: Date.now(),
            })
          }
          // Any phone not in the response is definitely not linked
          for (const phone of uncachedPhones) {
            if (!linkCache.has(phone)) {
              linkCache.set(phone, {
                data: { linked: false, has_orcamento: false, has_compromisso_aberto: false, is_fidelizado: false, clientes: [], auto_match_candidates: [] },
                cachedAt: Date.now(),
              })
            }
          }
        }
      } catch (err) {
        log('useMessagingChatCliente', 'resolveChatTagsBulk', 'Error — caching phones as unlinked for 30s to prevent retry storm', err, 'error')
        const failureEntry = {
          data: { linked: false, has_orcamento: false, has_compromisso_aberto: false, is_fidelizado: false, clientes: [], auto_match_candidates: [] },
          cachedAt: Date.now() - (LINK_CACHE_TTL_MS - LINK_CACHE_FAILURE_TTL_MS),
        }
        for (const phone of uncachedPhones) {
          if (!linkCache.has(phone)) {
            linkCache.set(phone, failureEntry)
          }
        }
      } finally {
        for (const phone of uncachedPhones) pendingPhones.delete(phone)
      }
    }

    // Build the result Map from cache
    const result = new Map()

    // WhatsApp entries: derive tags from cache
    for (const entry of whatsappEntries) {
      const phone = entry.chat.phoneNumber.replace(/\D/g, '')
      const cached = getCached(phone) || { linked: false, has_orcamento: false, has_compromisso_aberto: false, is_fidelizado: false }
      result.set(entry.key, deriveTags(cached))
    }

    // Non-WhatsApp entries: empty tags
    for (const entry of nonWhatsappEntries) {
      result.set(entry.key, [])
    }

    return result
  }

  return {
    lookupChatCliente,
    linkChatCliente,
    unlinkChatCliente,
    resolveChatActions,
    resolveChatTags,
    resolveChatTagsBulk,
  }
}
